import {
  PathPickMarketSyncKind,
  PathPickFilterBrand,
  PathPickProduct,
  PathPickProductChannelStatus,
  PathPickProductKey,
  PathPickProductRequests,
  PathPickProductRequestKind,
  PathPickProductRequestSlot,
  PathPickProductType,
  PathPickPopularProduct,
  PopularProductSeed,
} from "@/features/pathpick/types";

// 마켓(크림/포이즌) 지표 계산은 utils/market.ts 가 담당한다.
// 호출부가 import 경로를 둘로 나누지 않도록 이 배럴에서 그대로 내보낸다.
export * from "@/features/pathpick/utils/market";

// 선택 상태·수량처럼 상품별로 값을 들고 있는 곳의 키 — 숫자 id만 쓰면 두 유형의 같은 id가 하나로 합쳐진다.
export const toPathPickProductKey = (
  type: PathPickProductType,
  id: number
): PathPickProductKey => `${type}:${id}`;

// 상품 자체 이미지가 없으면 SKU 로 모인 AI 수집 이미지로 대신한다.
// (핫딜 상품은 자체 이미지가 아예 없는 경우가 흔하다)
// 세트에 'AI 이미지 우선 노출'이 켜져 있으면 반대로 AI 이미지를 먼저 쓰고, 없을 때 원본으로 대신한다.
export const getProductImageUrl = (item: PathPickProduct) =>
  item.preferAiImage
    ? item.skuImageUrls?.[0] ?? item.imageUrls?.[0]
    : item.imageUrls?.[0] ?? item.skuImageUrls?.[0];

// 위에서 고른 대표 이미지가 AI 수집 이미지인지 — 화면에서 AI 배지를 달지 판단한다.
export const isProductImageAiCollected = (item: PathPickProduct) =>
  item.preferAiImage
    ? !!item.skuImageUrls?.[0]
    : !item.imageUrls?.[0] && !!item.skuImageUrls?.[0];

export const getProductSizes = (item: PathPickProduct) =>
  item.options.map((option) => ({
    size: option.displayName || option.name,
    stock: option.stock,
  }));

export const getProductTotalStock = (item: PathPickProduct) =>
  item.options.reduce((sum, option) => sum + option.stock, 0);

// 품절 판정은 서버 플래그 하나만 근거로 삼는다(재고 합계로 유추하지 않는다).
// 필드가 없으면 판매 가능 — 판정이 흩어지면 카드 표시와 신청 차단이 어긋난다.
export const isPathPickProductSoldOut = (
  item: Pick<PathPickProduct, "available">
) => item.available === false;

// filter-info 브랜드 → 셀렉트/필터에 쓸 수 있는 항목만.
// 명세상 id/name이 nullable이라 옵션 값으로 못 쓰는 항목을 여기서 한 번에 걸러낸다.
export const toPathPickBrandOptions = (
  brands: PathPickFilterBrand[] | undefined
): { id: number; name: string }[] =>
  (brands ?? []).flatMap((brand) =>
    brand.id !== null && brand.name !== null
      ? [{ id: brand.id, name: brand.name }]
      : []
  );

// 날짜 문자열의 앞부분만 읽어 "yyyy.MM.dd" / "yyyy-MM-dd" / ISO datetime을 모두 받는다
// — 마감일 필드의 값 형식이 명세에 없어 구분자와 시각 유무를 가정하지 않는다.
const DATE_PREFIX_PATTERN = /^(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/;

// 날짜 문자열과 오늘의 날짜 차이(일). 시간 성분을 제거한 자정 기준 비교.
// 파싱 실패 시 null.
export const getDdayFromDateString = (date: string): number | null => {
  const matched = DATE_PREFIX_PATTERN.exec(date.trim());
  if (!matched) return null;
  const [, year, month, day] = matched.map(Number);
  // 0월/0일은 Date가 이전 달로 굴려버려 조용히 엉뚱한 D-day가 나오므로 여기서 막는다.
  if (!year || !month || !day) return null;
  const target = new Date(year, month - 1, day);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  return Math.round(
    (target.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24)
  );
};

// 엑셀 채널 표기 — 로케일과 무관한 채널 마크라 번역하지 않는다.
const NAVER_CHANNEL_SUFFIX = "N";
const KREAM_CHANNEL_SUFFIX = "K";

// 신청 종류 → requests의 슬롯 키. 신청 종류가 늘면 여기서 컴파일 에러가 나 매핑을 정하고 지나가게 된다.
const REQUEST_KIND_SLOT: Record<
  PathPickProductRequestKind,
  keyof PathPickProductRequests
> = {
  NAVER_SYNC: "naverSync",
  MUSTIT_SYNC: "mustitSync",
  TRENBE_SYNC: "trenbeSync",
  CAFE24_SYNC: "cafe24Sync",
  NAVER_EXCEL: "naverExcel",
  KREAM_EXCEL: "kreamExcel",
  KREAM_EXCEL_CONFIRMED_QUANTITY: "kreamExcelConfirmedQuantity",
};

// 신청 종류 하나의 요청 슬롯 — 미신청이면 null(requests 자체가 없어도 같다).
// 슬롯 키를 아는 곳을 여기 하나로 모아 호출부가 requests 구조를 몰라도 되게 한다.
export const getPathPickRequestSlot = (
  requests: PathPickProductRequests | null | undefined,
  kind: PathPickProductRequestKind
): PathPickProductRequestSlot | null =>
  requests?.[REQUEST_KIND_SLOT[kind]] ?? null;

// 화면에서 신청·표시하는 마켓 연동 종류.
// 카드 배지와 연동 채널 선택(권한 있는 마켓만 — usePathPickSyncKinds)이 같은 목록을 쓴다 — 순서는 채널 선택 표시 순서.
export const PATH_PICK_MARKET_SYNC_KINDS: PathPickMarketSyncKind[] = [
  "NAVER_SYNC",
  "TRENBE_SYNC",
  "MUSTIT_SYNC",
  "CAFE24_SYNC",
];

// 상품 카드 상태 배지의 표시 조건 — 완료/처리중/실패는 서로 중복 노출될 수 있다.
export interface PathPickProductBadgeState {
  // 연동 완료 (마켓 연동 슬롯 중 하나라도 isCompleted)
  isSyncCompleted: boolean;
  // 파일 생성 완료된 엑셀 채널 표기 — ["N"] | ["K"] | ["N", "K"]
  fileReadySuffixes: string[];
  isProcessing: boolean;
  hasFailure: boolean;
  // 확인 필요 배지가 열 요청 상세의 id — 실패 슬롯이 없거나 서버가 id를 주지 않으면 null.
  // null이면 배지는 클릭할 수 없다(열 대상이 없다).
  failedRequestId: number | null;
}

type ChannelBadgeKind = "completed" | "processing" | "failed";

// 채널 상태 → 카드 배지 분류. 아직 처리 전(PENDING)도 사용자에겐 처리 중이다.
// 상태가 늘면 여기서 컴파일 에러가 나 분류를 정하고 지나가게 된다 (빠뜨리면 배지가 아예 안 뜬다).
const CHANNEL_STATUS_BADGE_KIND: Record<
  PathPickProductChannelStatus,
  ChannelBadgeKind
> = {
  SUCCESS: "completed",
  PENDING: "processing",
  PROCESSING: "processing",
  FAILED: "failed",
};

// 실패한 슬롯 중 배지가 열어야 할 요청 하나를 고른다.
// 실패 채널이 여러 개여도 배지는 하나뿐이라 가장 최근에 신청한 것을 고른다 — 사용자가 방금 한
// 신청의 결과를 먼저 확인하려 한다. 신청일이 없는 슬롯은 뒤로, 같으면 슬롯 나열 순서를 따른다.
const getFailedRequestId = (
  slots: (PathPickProductRequestSlot | null)[]
): number | null => {
  const failed = slots.filter(
    (slot): slot is PathPickProductRequestSlot =>
      slot?.status === "FAILED" && slot.requestId != null
  );

  // 정렬 전 원본 순서가 동순위 tie-break이 되도록 안정 정렬(Array.sort)에 그대로 맡긴다.
  const [latest] = [...failed].sort((a, b) => {
    if (a.requestedAt === b.requestedAt) {
      return 0;
    }
    // 신청일이 없는 쪽을 뒤로 보낸다.
    if (!a.requestedAt) {
      return 1;
    }
    if (!b.requestedAt) {
      return -1;
    }
    return a.requestedAt < b.requestedAt ? 1 : -1;
  });

  return latest?.requestId ?? null;
};

// 요청 슬롯으로 카드 배지 표시 조건을 계산한다. 슬롯이 없으면 미신청이라 아무 배지도 만들지 않는다.
// 완료 배지는 isCompleted로, 처리중/실패 배지는 status로 판단한다 — 연동 슬롯은 BASIC 플랜에서
// status가 내려오지 않아 status로 완료를 보면 연동 완료 배지가 사라진다.
export const getPathPickProductBadgeState = (
  product: Pick<PathPickProduct, "requests">
): PathPickProductBadgeState => {
  // 마켓 연동은 어느 마켓이든 같은 "API 연동" 배지로 묶는다.
  const syncSlots = PATH_PICK_MARKET_SYNC_KINDS.map((kind) =>
    getPathPickRequestSlot(product.requests, kind)
  );
  const naverExcel = getPathPickRequestSlot(product.requests, "NAVER_EXCEL");
  const kreamExcel = getPathPickRequestSlot(product.requests, "KREAM_EXCEL");
  // 주문 확정 수량 기준 신청도 KREAM 파일이라 위 슬롯과 같은 배지로 묶어 본다.
  const kreamExcelConfirmedQuantity = getPathPickRequestSlot(
    product.requests,
    "KREAM_EXCEL_CONFIRMED_QUANTITY"
  );
  const kreamSlots = [kreamExcel, kreamExcelConfirmedQuantity];
  const allSlots = [...syncSlots, naverExcel, ...kreamSlots];

  const hasBadgeKind = (kind: ChannelBadgeKind) =>
    allSlots.some((slot) => {
      const status = slot?.status;
      return status != null && CHANNEL_STATUS_BADGE_KIND[status] === kind;
    });

  const fileReadySuffixes = [
    naverExcel?.isCompleted ? NAVER_CHANNEL_SUFFIX : null,
    // 두 KREAM 종류 중 하나라도 완료면 K 하나만 붙인다.
    kreamSlots.some((slot) => slot?.isCompleted) ? KREAM_CHANNEL_SUFFIX : null,
  ].filter((suffix): suffix is string => suffix !== null);

  return {
    isSyncCompleted: syncSlots.some((slot) => slot?.isCompleted),
    fileReadySuffixes,
    isProcessing: hasBadgeKind("processing"),
    hasFailure: hasBadgeKind("failed"),
    failedRequestId: getFailedRequestId(allSlots),
  };
};

// 목데이터 시드 → 인기 상품 목 객체 생성 (TODO: API 연결 시 제거)
export const createPopularProduct = (
  seed: PopularProductSeed
): PathPickPopularProduct => ({
  boutiqueId: 1,
  id: seed.id,
  productType: "PRODUCT",
  isBuyerOrdered: false,
  brandName: seed.brandName,
  sku: seed.sku,
  name: seed.name,
  categories: seed.categories,
  pathradeCategory: seed.categories[0] ?? "-",
  deliveryType: "STOCK",
  season: seed.season,
  color: seed.color,
  material: "-",
  gender: "남성",
  imageUrls: [],
  salePrice: { currency: "KRW", value: seed.price },
  madeIn: "-",
  marketPrice: { currency: "KRW", amount: seed.kreamPrice },
  options: seed.sizes.map((size) => ({
    name: size.name,
    displayName: size.name,
    stock: size.stock,
  })),
  lineSheetProductSku: null,
  isFavorite: false,
  isNew: false,
  isInBasket: false,
  requests: null,
  sizes: seed.sizes,
  kream: {
    productId: seed.id,
    salesCount: seed.kreamBidCount,
    price: {
      currency: "KRW",
      lowestValue: seed.kreamPrice,
      lastValue: seed.kreamLastPrice ?? seed.kreamPrice,
    },
  },
  // 포이즌 시세를 주지 않은 시드는 "POIZON 미매칭" 카드가 된다 — 목데이터로 그 케이스도 보여준다.
  poizon:
    seed.poizonPrice != null
      ? {
          spuId: seed.id,
          sales_30d: seed.poizonSales30d ?? 0,
          price: {
            currency: "KRW",
            lowestValue: seed.poizonPrice,
            lastValue: seed.poizonLastPrice ?? seed.poizonPrice,
          },
        }
      : null,
  kreamBidCount: seed.kreamBidCount,
  expectedMargin: seed.expectedMargin,
  marginRate: seed.marginRate,
});
